import React, { useEffect, useRef, useCallback } from 'react';
import { IconShieldCheck, IconRefresh, IconAlertTriangle } from '@tabler/icons-react';
import { useTurnstile } from '../hooks/useTurnstile';

// Verificação de dependências
const checkDependencies = () => {
  const issues = [];
  
  if (typeof window === 'undefined') {
    issues.push('Window object not available');
  }
  
  if (typeof document === 'undefined') {
    issues.push('Document object not available');
  }
  
  return issues;
};

const TurnstileWidget = ({ 
  onTokenChange, 
  onError, 
  onExpired,
  resetTrigger = 0,
  className = '',
  size = 'normal', // normal, compact
  theme = 'light' // light, dark, auto
}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const isRenderingRef = useRef(false);
  const hasRenderedRef = useRef(false);
  
  const {
    config,
    isReady,
    shouldShow,
    isLoading,
    error: configError,
    resetToken,
    clearState
  } = useTurnstile();

  // Handle successful verification
  const handleSuccess = useCallback((token) => {
    if (onTokenChange) {
      onTokenChange(token);
    }
  }, [onTokenChange]);

  // Handle verification error
  const handleError = useCallback((error) => {
    console.error('Turnstile verification error:', error);
    if (onError) {
      onError('Erro na verificação de segurança. Tente novamente.');
    }
    resetToken();
  }, [onError, resetToken]);

  // Handle token expiration
  const handleExpired = useCallback(() => {
    if (onExpired) {
      onExpired();
    }
    resetToken();
  }, [onExpired, resetToken]);

  // Render Turnstile widget
  const renderWidget = useCallback(() => {
    if (!isReady || !containerRef.current || isRenderingRef.current || widgetIdRef.current) {
      return;
    }

    // Verificar dependências antes de renderizar
    const dependencyIssues = checkDependencies();
    if (dependencyIssues.length > 0) {
      console.error('Turnstile dependencies not available:', dependencyIssues);
      handleError('Browser environment not supported');
      return;
    }

    // Verificar se window.turnstile existe e tem o método render
    if (!window.turnstile || typeof window.turnstile.render !== 'function') {
      console.error('Turnstile script not loaded or render method not available');
      handleError('Security verification script not loaded');
      return;
    }

    // Verificar se temos site_key
    if (!config?.site_key) {
      console.error('Turnstile site key not available');
      handleError('Security configuration incomplete');
      return;
    }

    try {
      isRenderingRef.current = true;
      
      const widgetId = window.turnstile.render(containerRef.current, {
        sitekey: config.site_key,
        callback: handleSuccess,
        'error-callback': handleError,
        'expired-callback': handleExpired,
        theme: theme,
        size: size
      });

      widgetIdRef.current = widgetId;
      hasRenderedRef.current = true;
    } catch (error) {
      console.error('Failed to render Turnstile widget:', error);
      handleError('Failed to load security verification');
    } finally {
      isRenderingRef.current = false;
    }
  }, [isReady, config, handleSuccess, handleError, handleExpired, theme, size]);

  // Reset widget
  const resetWidget = useCallback(() => {
    if (widgetIdRef.current !== null && window.turnstile && typeof window.turnstile.reset === 'function') {
      try {
        window.turnstile.reset(widgetIdRef.current);
        resetToken();
      } catch (error) {
        console.error('Failed to reset Turnstile widget:', error);
        // If reset fails, clear widget and re-render
        if (widgetIdRef.current !== null) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch (removeError) {
            console.error('Failed to remove widget during reset:', removeError);
          }
          widgetIdRef.current = null;
        }
        hasRenderedRef.current = false;
        setTimeout(() => {
          if (isReady && containerRef.current) {
            renderWidget();
          }
        }, 100);
      }
    } else {
      console.warn('Cannot reset Turnstile widget: widget not initialized or Turnstile not available');
    }
  }, [renderWidget, resetToken, isReady]);

  // Remove widget
  const removeWidget = useCallback(() => {
    if (widgetIdRef.current !== null && window.turnstile && typeof window.turnstile.remove === 'function') {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch (error) {
        console.error('Failed to remove Turnstile widget:', error);
      }
      widgetIdRef.current = null;
    } else if (widgetIdRef.current !== null) {
      console.warn('Cannot remove Turnstile widget: Turnstile not available, clearing reference');
      widgetIdRef.current = null;
    }
    hasRenderedRef.current = false;
    clearState();
  }, [clearState]);

  // Render widget when ready
  useEffect(() => {
    if (isReady && !isRenderingRef.current && !widgetIdRef.current && !hasRenderedRef.current) {
      renderWidget();
    }
    
    return () => {
      removeWidget();
    };
  }, [isReady]);

  // Handle reset trigger
  useEffect(() => {
    if (resetTrigger > 0) {
      resetWidget();
    }
  }, [resetTrigger, resetWidget]);

  // Handle manual reset button
  const handleManualReset = () => {
    resetWidget();
  };

  // Don't render anything if Turnstile is disabled
  if (!shouldShow) {
    return null;
  }

  // Render loading state
  if (isLoading) {
    return (
      <div className={`turnstile-widget ${className}`}>
        <div className="turnstile-loading">
          <div className="turnstile-skeleton"></div>
          <p className="turnstile-loading-text">Carregando verificação de segurança...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (configError || !isReady) {
    return (
      <div className={`turnstile-widget ${className}`}>
        <div className="turnstile-error">
          <div className="turnstile-error-content">
            <IconAlertTriangle size={20} />
            <span>Erro na verificação de segurança</span>
          </div>
          <button 
            type="button" 
            className="turnstile-retry-btn"
            onClick={handleManualReset}
          >
            <IconRefresh size={16} />
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // Render Turnstile widget
  return (
    <div className={`turnstile-widget ${className}`}>
      <div className="turnstile-header">
        <IconShieldCheck size={16} />
        <span>Verificação de segurança</span>
      </div>
      <div 
        ref={containerRef}
        className="turnstile-container"
        role="button"
        tabIndex={0}
      />
    </div>
  );
};

export default TurnstileWidget;